import type { PlayerId } from '@pixelworld/shared';

const STORAGE_KEY = 'pixelworld.mutedPlayers';

/**
 * Players this viewer does not want to read. Kept on the device, so a muted player
 * stays muted in the next room too. Muting only hides; it does not tell anyone.
 */
export class MutedPlayers {
  private readonly ids: Set<PlayerId>;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly storage: Pick<Storage, 'getItem' | 'setItem'> | null) {
    this.ids = new Set(this.load());
  }

  has(id: PlayerId): boolean {
    return this.ids.has(id);
  }

  toggle(id: PlayerId): void {
    if (this.ids.has(id)) this.ids.delete(id);
    else this.ids.add(id);
    this.save();
    for (const listener of this.listeners) listener();
  }

  /** Mutes without toggling: a reported player stays muted. */
  mute(id: PlayerId): void {
    if (!this.ids.has(id)) this.toggle(id);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private load(): string[] {
    try {
      const parsed: unknown = JSON.parse(this.storage?.getItem(STORAGE_KEY) ?? '[]');
      return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
    } catch {
      return [];
    }
  }

  private save(): void {
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify([...this.ids]));
    } catch {
      // Storage can be blocked; the choice then lasts until the page closes.
    }
  }
}

function browserStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export const mutedPlayers = new MutedPlayers(browserStorage());
